// Animated background component with styles
import React from 'react';

interface LoginBackgroundProps {
  canvasRef: React.RefObject<HTMLCanvasElement>;
  particlesRef: React.RefObject<HTMLDivElement>;
  isDark?: boolean;
}

export const LoginBackground: React.FC<LoginBackgroundProps> = ({ canvasRef, particlesRef }) => {
  return (
    <>
      <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }} />
      <div ref={particlesRef} className="fixed top-0 left-0 w-full h-full overflow-visible pointer-events-none" style={{ zIndex: 5 }} />
      
      <style>{`
        .layer-1 {
          background: radial-gradient(circle, rgba(102, 126, 234, 0.6), rgba(102, 126, 234, 0.1), transparent);
          animation: float-slow 30s infinite ease-in-out, pulse-glow 3s infinite ease-in-out;
          will-change: transform, opacity;
          box-shadow: 0 0 25px rgba(102, 126, 234, 0.5), 0 0 40px rgba(102, 126, 234, 0.3);
        }
        
        /* Mobile-optimized animations */
        @media (max-width: 768px) {
          .layer-1, .layer-2, .layer-3, .layer-4 {
            animation-duration: 40s, 4s;
          }
        }
        .layer-2 {
          background: radial-gradient(circle, rgba(118, 75, 162, 0.6), rgba(118, 75, 162, 0.15), transparent);
          animation: float-medium 22s infinite ease-in-out, pulse-glow 3.5s infinite ease-in-out;
          will-change: transform, opacity;
          box-shadow: 0 0 30px rgba(118, 75, 162, 0.5), 0 0 45px rgba(118, 75, 162, 0.3);
        }
        .layer-3 {
          background: radial-gradient(circle, rgba(237, 66, 100, 0.6), rgba(237, 66, 100, 0.15), transparent);
          animation: float-fast 16s infinite ease-in-out, pulse-glow 4s infinite ease-in-out;
          will-change: transform, opacity;
          box-shadow: 0 0 35px rgba(237, 66, 100, 0.5), 0 0 50px rgba(237, 66, 100, 0.3);
        }
        .layer-4 {
          background: radial-gradient(circle, rgba(79, 209, 197, 0.6), rgba(79, 209, 197, 0.15), transparent);
          animation: float-medium 20s infinite ease-in-out, pulse-glow 3.2s infinite ease-in-out;
          will-change: transform, opacity;
          box-shadow: 0 0 28px rgba(79, 209, 197, 0.5), 0 0 42px rgba(79, 209, 197, 0.3);
        }
        
        @keyframes pulse-glow {
          0%, 100% { 
            opacity: 0.8;
            filter: brightness(1);
          }
          50% { 
            opacity: 1;
            filter: brightness(1.3);
          }
        }
        @keyframes float-slow {
          0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
          25% { transform: translate3d(40px, -70px, 0) rotate(90deg); }
          50% { transform: translate3d(-40px, -140px, 0) rotate(180deg); }
          75% { transform: translate3d(70px, -70px, 0) rotate(270deg); }
        }
        @keyframes float-medium {
          0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
          25% { transform: translate3d(70px, -100px, 0) rotate(120deg); }
          50% { transform: translate3d(-70px, -200px, 0) rotate(240deg); }
          75% { transform: translate3d(100px, -100px, 0) rotate(360deg); }
        }
        @keyframes float-fast {
          0%, 100% { transform: translate3d(0, 0, 0) rotate(0deg); }
          25% { transform: translate3d(110px, -130px, 0) rotate(135deg); }
          50% { transform: translate3d(-110px, -260px, 0) rotate(270deg); }
          75% { transform: translate3d(130px, -130px, 0) rotate(405deg); }
        }
        
        .liquid-glass {
           backdrop-filter: blur(2px);
           -webkit-backdrop-filter: blur(2px);
           border: 2px solid rgba(255, 255, 255, 0.12);
           box-shadow: 
             0 8px 40px 0 rgba(0, 0, 0, 0.3),
             inset 0 1px 0 0 rgba(255, 255, 255, 0.15),
             0 0 80px rgba(102, 126, 234, 0.2);
           position: relative;
           overflow: hidden;
           transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .dark .liquid-glass {
           background: linear-gradient(135deg, rgba(10, 10, 15, 0.85), rgba(26, 26, 46, 0.9));
           border: 2px solid rgba(255, 255, 255, 0.08);
           box-shadow: 
             0 8px 40px 0 rgba(0, 0, 0, 0.6),
             inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
             0 0 80px rgba(102, 126, 234, 0.15);
        }
        
        .liquid-glass > * {
          position: relative;
          z-index: 11;
          pointer-events: auto;
        }
        
        .liquid-glass::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transition: left 0.5s;
          z-index: 1;
        }
        
        .liquid-glass:hover::before {
          left: 100%;
        }
        
        .liquid-glass:hover {
          box-shadow: 
            0 12px 60px 0 rgba(0, 0, 0, 0.4),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.2),
            0 0 120px rgba(102, 126, 234, 0.4),
            0 0 60px rgba(118, 75, 162, 0.3);
          border-color: rgba(255, 255, 255, 0.2);
        }
        
        .dark .liquid-glass:hover {
          box-shadow: 
            0 12px 60px 0 rgba(0, 0, 0, 0.7),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.15),
            0 0 120px rgba(102, 126, 234, 0.3),
            0 0 60px rgba(118, 75, 162, 0.25);
          border-color: rgba(255, 255, 255, 0.15);
        }
        
        .liquid-glass::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            circle at center,
            rgba(102, 126, 234, 0.15) 0%,
            rgba(118, 75, 162, 0.1) 30%,
            transparent 60%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
          z-index: 0;
        }
        
        .liquid-glass:hover::after {
          opacity: 1;
        }
        
        /* Allow particles to show through the card edges */
        .liquid-glass {
          isolation: isolate;
        }
        
        /* Removed borderGlow animation for cleaner appearance */
        
        .glass-input {
          background: rgba(255, 255, 255, 0.05) !important;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.15) !important;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: white !important;
        }
        
        .dark .glass-input {
          background: rgba(255, 255, 255, 0.03) !important;
          border: 1px solid rgba(255, 255, 255, 0.1) !important;
        }
        
        .glass-input:hover {
          background: rgba(255, 255, 255, 0.08) !important;
          border-color: rgba(255, 255, 255, 0.25) !important;
          transform: translateY(-1px);
        }
        
        .dark .glass-input:hover {
          background: rgba(255, 255, 255, 0.05) !important;
          border-color: rgba(255, 255, 255, 0.2) !important;
        }
        
        .glass-input:focus,
        .glass-input:focus-within {
          background: rgba(255, 255, 255, 0.1) !important;
          border-color: rgba(102, 126, 234, 0.5) !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15) !important;
          transform: translateY(-1px);
        }
        
        .dark .glass-input:focus,
        .dark .glass-input:focus-within {
          background: rgba(255, 255, 255, 0.07) !important;
          border-color: rgba(102, 126, 234, 0.4) !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
        }
        
        .glass-input input {
          color: white !important;
        }
        
        .glass-input .ant-input-prefix {
          color: rgba(255, 255, 255, 0.6);
        }
        
        .glass-input input::placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        
        .glass-input input::-webkit-input-placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        
        .glass-input input::-moz-placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        
        .glass-input input:-ms-input-placeholder {
          color: rgba(255, 255, 255, 0.5) !important;
        }
        
        /* Light mode card styles */
        .light-card {
          background: rgba(255, 255, 255, 0.98);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 2px solid rgba(102, 126, 234, 0.3);
          box-shadow: 
            0 8px 40px 0 rgba(0, 0, 0, 0.12),
            inset 0 1px 0 0 rgba(255, 255, 255, 1),
            0 0 80px rgba(102, 126, 234, 0.2);
          position: relative;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .light-card > * {
          position: relative;
          z-index: 11;
          pointer-events: auto;
        }
        
        .light-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.1), transparent);
          transition: left 0.5s;
          z-index: 1;
        }
        
        .light-card:hover::before {
          left: 100%;
        }
        
        .light-card:hover {
          transform: scale(1.02);
          box-shadow: 
            0 12px 60px 0 rgba(0, 0, 0, 0.18),
            inset 0 1px 0 0 rgba(255, 255, 255, 1),
            0 0 120px rgba(102, 126, 234, 0.3),
            0 0 60px rgba(118, 75, 162, 0.25);
          border-color: rgba(102, 126, 234, 0.4);
        }
        
        .light-card::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: radial-gradient(
            circle at center,
            rgba(102, 126, 234, 0.08) 0%,
            rgba(118, 75, 162, 0.05) 30%,
            transparent 60%
          );
          opacity: 0;
          transition: opacity 0.4s ease;
          pointer-events: none;
          z-index: 0;
        }
        
        .light-card:hover::after {
          opacity: 1;
        }
        
        .light-card {
          isolation: isolate;
        }
        
        /* Removed lightBorderGlow animation for cleaner appearance */
        
        /* Light mode input styles */
        .light-card .glass-input {
          background: rgba(102, 126, 234, 0.08) !important;
          border: 2px solid rgba(102, 126, 234, 0.25) !important;
          color: #1a1a2e !important;
        }
        
        .light-card .glass-input:hover {
          background: rgba(102, 126, 234, 0.12) !important;
          border-color: rgba(102, 126, 234, 0.35) !important;
        }
        
        .light-card .glass-input:focus,
        .light-card .glass-input:focus-within {
          background: rgba(102, 126, 234, 0.15) !important;
          border-color: rgba(102, 126, 234, 0.6) !important;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2) !important;
        }
        
        .light-card .glass-input input {
          color: #1a1a2e !important;
          font-weight: 500 !important;
        }
        
        .light-card .glass-input .ant-input-prefix {
          color: #667eea !important;
        }
        
        .light-card .glass-input input::placeholder {
          color: rgba(102, 126, 234, 0.5) !important;
        }
        
        .light-card .glass-input input::-webkit-input-placeholder {
          color: rgba(102, 126, 234, 0.5) !important;
        }
        
        .light-card .glass-input input::-moz-placeholder {
          color: rgba(102, 126, 234, 0.5) !important;
        }
        
        .light-card .glass-input input:-ms-input-placeholder {
          color: rgba(102, 126, 234, 0.5) !important;
        }
        
        /* Light mode OTP input */
        .light-card .ant-otp input {
          background: rgba(102, 126, 234, 0.08) !important;
          border: 2px solid rgba(102, 126, 234, 0.25) !important;
          color: #1a1a2e !important;
          font-weight: 600 !important;
        }
        
        .light-card .ant-otp input:focus {
          border-color: rgba(102, 126, 234, 0.6) !important;
          box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.2) !important;
        }
        
        /* Light mode text colors */
        .light-card h2,
        .light-card .ant-typography {
          color: #1a1a2e !important;
          font-weight: 700 !important;
        }
        
        /* Brand title in light mode */
        .light-card > form > div:first-child .ant-typography {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          font-weight: 800 !important;
        }
        
        .light-card p,
        .light-card label,
        .light-card span {
          color: #2d3748 !important;
          font-weight: 500 !important;
        }
        
        .light-card a {
          color: #667eea !important;
          font-weight: 600 !important;
        }
        
        .light-card a:hover {
          color: #5568d3 !important;
          text-decoration: underline;
        }
        
        /* Light mode button styles */
        .light-card button.ant-btn {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          border: none !important;
          color: white !important;
          font-weight: 700 !important;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
        }
        
        .light-card button.ant-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #5568d3 0%, #6a4190 100%) !important;
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(102, 126, 234, 0.5) !important;
        }
        
        .light-card button.ant-btn:active:not(:disabled) {
          transform: translateY(0px);
          box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4) !important;
        }
        
        .light-card .ant-btn-loading-icon {
          color: white !important;
        }
        
        .light-card button.ant-btn span {
          color: white !important;
          font-weight: 700 !important;
        }
        
        /* Light mode icon colors */
        .light-card .ant-input-prefix .anticon {
          color: #667eea !important;
          font-size: 16px;
        }
        
        .light-card button .anticon {
          color: white !important;
          font-size: 16px;
        }
        
        .light-card .ant-input-suffix .anticon {
          color: #667eea !important;
          font-size: 16px;
        }
        
        /* Light mode error message */
        .light-card .ant-typography[class*="text-red"] {
          color: #dc2626 !important;
          background: rgba(220, 38, 38, 0.1) !important;
          border: 1px solid rgba(220, 38, 38, 0.2);
          font-weight: 600 !important;
        }
        
        /* Light mode version text */
        .light-card > form > .ant-typography:last-child {
          color: #64748b !important;
          font-weight: 400 !important;
        }
      `}</style>
    </>
  );
};
